import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const InvoiceMobileCard = ({
  invoice,
  isSelected = false,
  onSelectionChange,
  className = ''
}) => {
  const navigate = useNavigate();

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbol = currency === 'EUR' ? '€' : '£';
    return `${symbol}${amount?.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-success';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-error';
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'reviewed':
        return { status: 'reviewed', variant: 'default' };
      case 'needs_review':
        return { status: 'needs_review', variant: 'default' };
      case 'exported':
        return { status: 'reviewed', variant: 'default' };
      default:
        return { status: 'processing', variant: 'default' };
    }
  };

  const handleCardClick = () => {
    navigate('/invoice-editor', { state: { invoiceId: invoice?.id } });
  };

  const isReviewed = invoice?.status === 'reviewed' || invoice?.status === 'exported';
  const statusConfig = getStatusConfig(invoice?.status);

  return (
    <div
      className={`
        bg-card border border-border rounded-lg p-4 transition-smooth
        ${isReviewed ? 'bg-success/5 border-success/20' : ''}
        ${isSelected ? 'ring-2 ring-primary/20 bg-primary/5' : ''}
        ${className}
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3 flex-1">
          <Checkbox
            checked={isSelected}
            onChange={(e) => onSelectionChange(invoice?.id, e?.target?.checked)}
            onClick={(e) => e?.stopPropagation()}
          />
          
          <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={18} className="text-muted-foreground" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{invoice?.vendor}</h3>
            <p className="text-sm text-muted-foreground truncate">{invoice?.filename}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <StatusIndicator 
            status={statusConfig?.status}
            variant={statusConfig?.variant}
            size="sm"
            confidence={invoice?.confidence}
          />
          {isReviewed && (
            <Icon name="CheckCircle" size={16} className="text-success" />
          )}
        </div>
      </div>
      {/* Invoice Details */}
      <div className="space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Invoice Number</p>
            <p className="font-mono text-sm text-foreground">{invoice?.invoiceNumber}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Date</p>
            <p className="text-sm text-foreground">{formatDate(invoice?.date)}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Net</p>
            <p className="font-mono text-sm text-foreground">
              {formatCurrency(invoice?.netAmount, invoice?.currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">VAT</p>
            <p className="font-mono text-sm text-foreground">
              {formatCurrency(invoice?.vatBreakdown?.reduce((sum, vat) => sum + vat?.amount, 0), invoice?.currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="font-mono text-sm font-medium text-foreground">
              {formatCurrency(invoice?.totalAmount, invoice?.currency)}
            </p>
          </div>
        </div>

        {/* VAT Breakdown */}
        {invoice?.vatBreakdown?.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">VAT Breakdown</p>
            <div className="flex flex-wrap gap-2">
              {invoice?.vatBreakdown?.map((vat, index) => (
                <div key={index} className="flex items-center space-x-1 bg-muted px-2 py-1 rounded text-xs">
                  <span>{vat?.rate}%:</span>
                  <span className="font-mono">{formatCurrency(vat?.amount, invoice?.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Confidence Score */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-muted-foreground">Confidence</p>
            <span className={`font-mono text-xs ${getConfidenceColor(invoice?.confidence)}`}>
              {Math.round(invoice?.confidence * 100)}%
            </span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${
                invoice?.confidence >= 0.9 ? 'bg-success' : 
                invoice?.confidence >= 0.7 ? 'bg-warning' : 'bg-error'
              }`}
              style={{ width: `${invoice?.confidence * 100}%` }}
            />
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          iconName="Edit"
          iconPosition="left"
          onClick={handleCardClick}
          fullWidth
          className="mr-2"
        >
          Edit
        </Button>
        
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            iconName="Eye"
            onClick={() => navigate('/invoice-editor', { state: { invoiceId: invoice?.id, viewOnly: true } })}
            className="h-8 w-8 p-0"
          />
          <Button
            variant="ghost"
            size="sm"
            iconName="Download"
            onClick={() => {/* Handle export */}}
            className="h-8 w-8 p-0"
          />
        </div>
      </div>
    </div>
  );
};

export default InvoiceMobileCard;
